import { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
    Box,
    Drawer,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { StarjackTable } from '@/components/Table/v2/Table';
import { HospitalityUserWithCredentials } from '@/models/hospitality';
import { HospitalityDrawer } from '@/pageContents/hospitality/HospitalityDrawer';
import { usePagination } from '@/hooks/usePagination';
import { useGetHospitalityUsersQuery } from '@/api/v2/hospitality';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import { useNotification } from '@starjack/features/notifications';
import { HospitalityDeleteDialog } from '@/pageContents/hospitality/HospitalityDeleteDialog';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useNavigate, useSearchParams } from 'react-router-dom';

type HospitalityContentProps = {
    newOrEditMode: boolean;
};
export const HospitalityContent: FunctionComponent<HospitalityContentProps> = ({
    newOrEditMode,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const [hospitalityToDelete, setHospitalityToDelete] = useState<{
        toDelete: HospitalityUserWithCredentials;
        isOpen: boolean;
    }>({ toDelete: null, isOpen: false });
    const { data, isLoading: hospitalityUsersLoading } =
        useGetHospitalityUsersQuery();
    const { currentItems, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            items: data?.hospitalityUsers,
            itemsPerPage: 10,
        });
    const hospitalityToEdit = useRef<HospitalityUserWithCredentials | null>(
        null
    );
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');
    const { addNotification } = useNotification();

    const closeDrawer = () => {
        setIsOpen(false);

        setTimeout(() => {
            navigate('/hospitality');
        }, 100);
    };

    useEffect(() => {
        setPageData({
            title: 'Hospitality',
            body: (
                <>
                    <Typography>
                        Countermen, die mit starjack.hospitality den
                        Ticketverkauf in den Beherbergungsbetrieben umsetzen,
                        verwalten hier die Zugänge für die einzelnen
                        Beherbergungsbetriebe.
                        <br />
                        Die hier generierten Zugangsdaten pflegen Sie
                        anschließend in der Software starjack.hospitality ein
                        und aktivieren damit den Verkauf für das Hotel bzw. den
                        Beherbergungsbetrieb.
                    </Typography>
                </>
            ),
        });
    }, []);

    useEffect(() => {
        if (newOrEditMode) {
            const isEdit = window.location.pathname.includes(
                'hospitality/bearbeiten'
            );

            if (isEdit && data) {
                const id = searchParams.get('id');

                const hospitalityUser = data.hospitalityUsers.find(
                    (hospitality) => hospitality.id === id
                );

                if (hospitalityUser) {
                    hospitalityToEdit.current = hospitalityUser;
                } else {
                    addNotification({
                        type: 'error',
                        message: 'Ungültige Id',
                    });

                    return;
                }
            }

            setTimeout(() => {
                setIsOpen(true);
            }, 100);
        }
    }, [data]);

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Hospitality
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <LoadingButton
                        onClick={() => {
                            navigate('hospitality/neu');
                        }}
                        variant='contained'
                        loading={false}
                    >
                        + Neue Verkaufstelle für hospitality anlegen
                    </LoadingButton>
                </Box>
                <Drawer anchor='right' open={isOpen} onClose={closeDrawer}>
                    <HospitalityDrawer
                        editHospitality={hospitalityToEdit.current || null}
                        onClose={closeDrawer}
                    />
                </Drawer>
                <StarjackTable<HospitalityUserWithCredentials>
                    count={data?.hospitalityUsers.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Benutzername',
                            itemKey: 'username',
                        },
                        {
                            label: 'Name',
                            itemKey: 'name',
                        },
                        {
                            label: 'DTA Verkaufsstelle',
                            itemTransform: (item) =>
                                item?.dtaPointOfSaleName
                                    ? item.dtaPointOfSaleName
                                    : '-',
                        },
                        {
                            label: 'BEI Benutzername',
                            itemTransform: (item) =>
                                item?.beiUsername ? item.beiUsername : '-',
                        },
                        {
                            label: 'BEI Passwort',
                            itemTransform: (item) =>
                                item?.beiPassword ? item.beiPassword : '-',
                        },
                        {
                            label: 'Status',
                            itemTransform: (item) => {
                                return item.enabled
                                    ? 'Aktiviert'
                                    : 'Deaktiviert';
                            },
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (item) => (
                                <Stack spacing={2} direction='row'>
                                    <IconButton
                                        onClick={() => {
                                            navigate(
                                                `hospitality/bearbeiten?id=${item.id}`
                                            );
                                        }}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <EditIcon />
                                        </SvgIcon>
                                    </IconButton>

                                    <IconButton
                                        onClick={() => {
                                            setHospitalityToDelete({
                                                toDelete: item,
                                                isOpen: true,
                                            });
                                        }}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <DeleteIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Stack>
                            ),
                        },
                    ]}
                    itemId={'id'}
                    loading={hospitalityUsersLoading}
                    onPageChange={setNextPage}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                />

                <HospitalityDeleteDialog
                    onClose={() => {
                        setHospitalityToDelete((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    open={!!hospitalityToDelete.isOpen}
                    hospitality={hospitalityToDelete.toDelete}
                />
            </Stack>
        </>
    );
};
